import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
  createCustomer,
  getLatestCustomerCode
} from 'src/services/projectService';
import { getAllEmployees } from 'src/services/employeeService';
import { AppActions } from 'src/store/AppReducer';
import { RootStateType } from 'src/store/RootReducer';
import SearchIcon from '@mui/icons-material/Search';

export interface CustomerInformation {
  code: string;
  description: string;
  name: string;
  industry: string;
  region: string;
  engagementLead: any;
  status: any;
}

const AddCustomer = ({
  toggle,
  open,
  getData,
  length,
  segmentId,
  countries,
  segmentName,
  setSnackBarOpen,
  setSnackbarSeverity,
  setSnackBarMessage,
}) => {
  const dispatch = useDispatch();
  const TextFieldStyle = {
    paddingTop: 2,
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const LabelStyle = {
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const selectStyle = {
    width: '94%',
    marginLeft: 15,
    marginTop: 5,
    paddingRight: 15,
    fontSize: '13px'
  };

  const { register, handleSubmit, reset, getValues, setValue } =
    useForm<CustomerInformation>({
      defaultValues: {
        code: '',
        description: '',
        name: '',
        industry: '',
        region: '',
        engagementLead: ''
      }
    });
  const [nameError, setNameError] = useState('');
  const [descError, setDescError] = useState('');
  const [regionError, setRegionError] = useState('');
  const [industryError, setIndustryError] = useState('');
  const [engagementLeadError, setEngagementLeadError] = useState('');
  const [latestCode, setLatestCode] = useState('');
  const [employees, setEmployees] = useState([]);

  const [status, setStatus] = useState<any>('Active');
  const [statusError, setStatusError] = useState('');

  const getEmployeeData = () => {
    getAllEmployees().then((res) => {
      setEmployees((res.data as any[]) || []);
    });
  };

  const onClose = () => {
    reset();
    setRegionValue('');
    setValue('region', '');
    setValue('engagementLead', '');
    toggle();
    onClearErrorMessages();
    setEngagementLead('');
    setStatus('Active');
  };

  //clear inline error messages
  const onClearErrorMessages = ( () => {
    setNameError('');
    setRegionError('');
    setIndustryError('');
    setEngagementLeadError('');
    setStatusError('');
  })

  const [regionValue, setRegionValue] = useState('');
  const [engagementLead, setEngagementLead] = useState<any>('');

  const containsText = (text, searchText) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  const [searchText, setSearchText] = useState('');

  const displayedCountries = useMemo(
    () =>
    countries.length > 0 &&
    countries?.filter((option) => containsText(option, searchText)),
    [searchText, countries]
  );

  const displayedAccountManager = useMemo(
    () =>
    employees.length > 0 &&
    employees?.filter((option) => containsText(option.name, searchText)),
    [searchText, employees]
  );

  const useAppSelector: TypedUseSelectorHook<RootStateType> = useSelector;
  const { isLoading } = useAppSelector((state) => state.appReducer);

  const onSubmit = (data: CustomerInformation) => {
    if (
      data.name !== '' &&
      data.name.trim().length > 0 &&
      data.name.length < 31 &&
      data.description.length < 51 &&
      data.region !== '' &&
      data.industry !== '' &&
      data.industry.trim().length > 0 &&
      data.engagementLead !== "" && 
      data.status !== ''
    ) {
      dispatch(AppActions.showLoader());

      setNameError('');
      setRegionError('');
      setIndustryError('');
      setEngagementLeadError('');
      setStatusError('');

      const body = {
        code: latestCode,
        name: data.name,
        engagementLead: data?.engagementLead?.name,
        engagementLeadId: data?.engagementLead?.employeeId,
        segmentId: segmentId,
        description: data.description,
        country: data.region,
        industry: data.industry,
        isActive: data.status === 'Active' ? true : false
      };

      createCustomer(body)
        .then(() => {
          reset();
          setRegionValue('');
          setEngagementLead('');
          setSnackBarMessage('You have added new client details successfully.');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          getData();
          toggle();
          dispatch(AppActions.hideLoader());
        })
        .catch((error) => {
          dispatch(AppActions.hideLoader());
          setSnackBarMessage(error?.request?.response ? error?.request?.response : 'Unable to add the client');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
        });
    } if (data.name === '') {
      setNameError('Client name required');
    } else if (data.name.trim().length < 1) {
      setNameError('Client name cannot be only spaces');
    } if (data.region === '') {
      setRegionError('Country is required');
    } if (data.industry === '') {
      setIndustryError('Industry required');
    } else if (data.industry.trim().length < 1) {
      setIndustryError('Industry cannot be only spaces');
    } if (data?.engagementLead === "") {
      setEngagementLeadError('Engagement Lead is required');
    }
  };

  const getLatestAccountValue = async () => {
    const result: any = await getLatestCustomerCode();
    const value = result.data.replace(/\D/g, '');

    const padLeft = (nr?: any, n?: any, str?: any) => {
      return Array(n - String(nr).length + 1).join(str || '0') + nr;
    };

    if (value === '001' && length < 1) {
      setLatestCode(`C${value}`);
    } else {
      setLatestCode(`C${padLeft(Number(value) + 1, 3)}`);
    }
  };

  useEffect(() => {
    if (open) {
      getEmployeeData();
    }
  }, [open]);

  useEffect(() => {
    if (open) getLatestAccountValue();
  }, [open, length]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid item xs={12} sx={{ pt: 3, pb: 1, pl: 2 }}>
        <Typography style={{ fontSize: '15px' }} variant="h3">
          Create New Client
        </Typography>
        <Typography style={{ fontSize: '14px', marginTop: 5 }}>
         {segmentName}
        </Typography>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Client Name<span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            id="customerName"
            name="customerName"
            inputProps={{ maxLength: 30 }}
            size="small"
            {...register('name', {})}
            style={TextFieldStyle}
            fullWidth
            placeholder="Enter Client Name"
            variant="outlined"
            onChange={(e) => {
              if (e.target.value.length > 30) {
                setNameError('Client name must not exceed 30 characters');
              } else {
                setNameError('');
              }
            }}
          />
          <Typography
            style={{
              paddingLeft: 15,
              paddingRight: 15,
              fontSize: '11px'
            }}
            color="red"
          >
            {nameError}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Client Code
          </Typography>
          <TextField
            id="customerCode"
            name="customerCode"
            className="disabledTextbox"
            aria-readonly
            size="small"
            sx={{
              input: {
                background: '#d1d1d1',
                borderRadius: 0.5
              }
            }}
            {...register('code', {})}
            style={{
              ...TextFieldStyle,
              pointerEvents: 'none'
            }}
            fullWidth
            placeholder="Client Code"
            variant="outlined"
            value={latestCode}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Country<span style={{ color: 'red' }}>*</span>
          </Typography>
          <Select
            id="customerCountryName"
            MenuProps={{ autoFocus: false }}
            className="select-text"
            labelId="search-select-label"
            size="small"
            style={selectStyle}
            displayEmpty
            inputProps={{ 'aria-label': 'Without label' }}
            {...register('region')}
            defaultValue={''}
            value={regionValue || ''}
            onChange={(e) => {
              setRegionValue(e.target.value);
              if(e.target.value) {
                setRegionError('')
              }
            }}
            onClose={() => setSearchText('')}
          >
            <ListSubheader>
              <TextField
               id="searchCustomerCountryName"
               size="small"
               autoFocus
               placeholder="Type to search..."
               fullWidth
               InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
              ></TextField>
            </ListSubheader>
            <MenuItem style={{ display: 'none' }} selected disabled value={''}>
              Enter Country
            </MenuItem>
            {displayedCountries.length > 0 &&
              displayedCountries?.map((option, i) => (
                <MenuItem key={i} value={option}>
                  {option}
                </MenuItem>
              ))}
          </Select>
          <Typography
            style={{
              paddingLeft: 15,
              paddingRight: 15,
              fontSize: '11px'
            }}
            color="red"
          >
            {regionError}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Client Industry<span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            id="customerIndustryName"
            name="customerIndustryName"
            inputProps={{ maxLength: 30 }}
            size="small"
            {...register('industry', {})}
            style={TextFieldStyle}
            fullWidth
            placeholder="Enter Industry Name"
            variant="outlined"
            onChange={(e) => {
              if (e.target.value.length > 30) {
                setIndustryError('Industry must not exceed 30 characters');
              } else {
                setIndustryError('');
              }
            }}
          />
          <Typography
            style={{
              paddingLeft: 15,
              paddingRight: 15,
              fontSize: '11px'
            }}
            color="red"
          >
            {industryError}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Engagement Lead<span style={{ color: 'red' }}>*</span>
          </Typography>
          <Select
            id="engagementLead"
            MenuProps={{ autoFocus: false }}
            labelId="search-select-label"
            className="select-text"
            size="small"
            style={selectStyle}
            displayEmpty
            inputProps={{ 'aria-label': 'Without label' }}
            {...register('engagementLead')}
            defaultValue={''}
            value={engagementLead || ''}
            onChange={(e) => {
              setEngagementLead(e.target.value);
              if(e.target.value) {
                setEngagementLeadError('');
              }
            }}
            onClose={() => setSearchText('')}
          >
             <ListSubheader>
              <TextField
               id="searchEngagementLead"
               size="small"
               autoFocus
               placeholder="Type to search..."
               fullWidth
               InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
              ></TextField>
            </ListSubheader>
            <MenuItem style={{ display: 'none' }} selected disabled value={''}>
              Enter Engagement Lead
            </MenuItem>
            {displayedAccountManager.length > 0 &&
              displayedAccountManager?.map((option, i) => (
                <MenuItem key={i} value={option}>
                  {option.name}
                </MenuItem>
              ))}
          </Select>
          <Typography
            style={{
              paddingLeft: 15,
              paddingRight: 15,
              fontSize: '11px'
            }}
            color="red"
          >
            {engagementLeadError}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Description
          </Typography>
          <TextField
            id="customerDescription"
            name="customerDescription"
            inputProps={{ maxLength: 50 }}
            size="small"
            {...register('description', {})}
            style={TextFieldStyle}
            fullWidth
            placeholder="Enter Description"
            variant="outlined"
            onChange={(e) => {
              if (e.target.value.length < 51) {
                setDescError('');
              } else {
                setDescError(
                  'Account description must not exceed 50 characters'
                );
              }
            }}
            rows={2}
            multiline
          />
          <Typography
            style={{
              paddingLeft: 15,
              paddingRight: 15,
              fontSize: '11px'
            }}
            color="red"
          >
            {descError}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Status<span style={{ color: 'red' }}>*</span>
          </Typography>
          <Select
            id="status"
            MenuProps={{ autoFocus: false }}
            labelId="search-select-label"
            className="select-text"
            size="small"
            style={selectStyle}
            displayEmpty
            inputProps={{ 'aria-label': 'Without label' }}
            {...register('status', {})}
            defaultValue={''}
            value={status || ''}
            onChange={(e) => {
              setStatus(e.target.value);
              if(e.target.value) {
                setStatusError('');
              }
            }}
          >
            <MenuItem value="Active">
              Active
            </MenuItem>
            <MenuItem value="Inactive">
              Inactive
            </MenuItem>
          </Select>
          <Typography
            style={{
              paddingLeft: 15,
              paddingRight: 15,
              fontSize: '11px'
            }}
            color="red"
          >
            {statusError}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            mb: 2,
            mt: 2,
            pr: 2,
            display: 'flex',
            justifyContent: 'flex-end'
          }}
          xs={12}
        >
          <Button
            id="customer-btn-onClose"
            sx={{
              width: '10%',
              fontWeight: 'normal',
              background: '#F5F7FF',
              fontSize: '13px'
            }}
            onClick={() => onClose()}
          >
            Cancel
          </Button>
          <Button
            id="customer-btn-onSubmit"
            disabled={isLoading}
            className={isLoading ? 'loading' : ''}
            sx={{
              ml: 2,
              width: '10%',
              fontSize: '13px',
              fontWeight: 'normal',
              background: '#0069E4'
            }}
            variant="contained"
            type="submit"
          >
            Add
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default AddCustomer;
